/**
 * Feature detection for the browser OPFS SQLite driver
 * (docs/offline/PHASE-8-ARCHITECTURE.md §1). Checked once, before ever
 * constructing `OpfsSqliteDriver` - a browser that fails this check stays on
 * `RemoteDataSource` exactly as it does today; there is no half-initialised
 * local mode a cashier could get stuck in.
 *
 * Built under `tsconfig.browser.json` alongside `opfsWorkerFactory.ts` (see
 * its file doc) because `navigator` and `Worker` are lib.dom names. The
 * runtime checks below are still always safe to call from any environment,
 * browser or not: `typeof x` never throws on an undeclared ambient global,
 * so this correctly returns `false` under Node/SSR rather than a
 * ReferenceError.
 *
 * Deliberately does NOT check for `FileSystemSyncAccessHandle` on this
 * (main-thread) scope: that interface is only ever exposed inside a
 * Worker's global scope, never on `window` - a main-thread lookup for it
 * reads as `undefined` in every browser, regardless of actual support, so
 * that check was silently forcing this function to always return `false`.
 * The real sync-access-handle usage happens inside `createOpfsWorker`'s
 * worker (see `opfsWorkerFactory.ts`), which is exactly where that global
 * is reachable. `getDirectory` + `Worker` are the only two capabilities
 * this main-thread check can actually observe; a worker that still fails
 * to open a sync access handle at runtime is a genuine unsupported-browser
 * case for `OpfsSqliteDriver` itself to surface, not something a
 * main-thread pre-check can catch from here.
 */
export function isOpfsSqliteSupported() {
    if (typeof navigator === 'undefined' || typeof navigator.storage?.getDirectory !== 'function') {
        return false;
    }
    if (typeof Worker === 'undefined')
        return false;
    return true;
}
/**
 * A best-effort request, not a guarantee (see the architecture doc's own
 * caveat) - the browser may still evict this origin's storage under disk
 * pressure. Call once, at local-mode enrolment.
 */
export async function requestPersistentStorage() {
    if (typeof navigator === 'undefined' || typeof navigator.storage?.persist !== 'function')
        return false;
    try {
        return await navigator.storage.persist();
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=opfsSupport.js.map